import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AsyncSelect from 'react-select/async';
import { reilmAPI } from '../../../services/apiClient';
import AddPropertyIcon from './AddPropertyIcon.jsx';
import { isHandledElsewhere } from '../../flip/requestErrors';

// No stage and no strategy key. The requirement is that the stage comes from the schema for every
// write path ("all new properties added shall automatically be considered New Lead"), and a form that
// still sent stage: 'new_lead' would be the one path where that default never fires - leaving the
// only path that matters untested by the thing meant to guarantee it. The backend default is in
// reilm-be/modules/properties/model.js. strategy has no default and is left absent rather than sent
// as '': PropertyDashboard resolves it through resolveSelectOption (features/pipeline/utils.js),
// which reads an absent value the same way stage does -- null, the select's placeholder.
//
// No contact key either, and this one is a data decision, not a form one. The form posted
// contact: [{ name:'', mobile:'', email:'', role:'', other:'' }] on every create, and that
// placeholder is what 34 of the 39 documents mr-07 deletes actually hold -- the 5 carrying a real
// value were all created in May and June. The count is what forces the order: the first scope of
// this collection found 36 and the 24.08 census found 39, the same set on two days, growing with
// every property created and no one typing a contact. So the deletion cannot run while the form
// still posts the key, or it undoes itself from the next create onward. Nothing reads the key back:
// mr-05 took the pipeline column, mr-06 took the Property Dashboard card, and
// modules/properties/validation.js refuses the path on PATCH.
const initialForm = {
  propertyType: '',
  price: '',
  address: {
    street: '',
    city: '',
    state: '',
    zip: '',
    coordinates: { lat: null, lon: null },
  },
  details: {
    beds: '',
    baths: '',
    sqft: '',
    lotSize: '',
    yearBuilt: '',
    hoaFee: '',
    sewer: '',
    parking: '',
    amenities: '',
    floodZone: '',
    tax: { amount: '', year: '' },
    schools: {
      elementary: '',
      elementary_state_rate: '',
      middle: '',
      middle_state_rate: '',
      high: '',
      high_state_rate: '',
    },
  },
};

// Keys are the provider's codes, values are the only four strings the property model's enum accepts,
// so this map may never emit anything else -- a value outside the four is rejected on save, not shown
// with a warning. The provider's vocabulary is the wider one and it is open-ended: 22 sampled detail
// responses on 11.08 returned `single_family`, `condos`, `apartment`, `industrial` and `other`. Only
// `condos` was a missed translation -- the map had the singular, so condos imported with the type
// blank while every other field filled. The rest have no counterpart among the four and deliberately
// map to nothing, for the user to pick; an unset type is savable for exactly this reason.
const PROPERTY_TYPE_LABELS = {
  single_family: 'Single Family',
  multi_family: 'Multi Family',
  condo: 'Condo',
  condos: 'Condo',
  townhouse: 'Townhouse',
};

// description.baths is the provider's own rounded integer; description.baths_consolidated carries
// the exact value ("3.5") and disagrees with it on 145 of 533 sampled detail responses (27.2%), 141
// of those rounded up. It is always a string in every sample, but three carry a non-numeric one
// ("2.5+", "1.5+") rather than a plain number -- and details.baths on the property model is
// `{ type: Number }`, so a raw "2.5+" reaching Property.create throws a CastError there, which is a
// 400 that loses the whole import, all sixteen mapped fields, not just this one (same failure shape
// as the propertyType comment above). Use the consolidated value only when it parses to a finite
// number; otherwise fall back to the rounded field exactly as before.
const resolveBaths = (description) => {
  const consolidated = description?.baths_consolidated;
  // Every sampled record carries this as a string; anything else (an array, a boolean, a bare
  // number) must not reach Number() at all -- Number([]) is 0 and Number(true) is 1, both finite,
  // so without this guard they would silently read as a real bath count instead of falling back.
  // Same reasoning as the backend's parseBathsConsolidated (scripts/lib/subject-baths-census.js),
  // which this mirrors.
  if (typeof consolidated !== 'string') return description?.baths ?? '';
  const trimmed = consolidated.trim();
  const parsed = Number(trimmed);
  // Number('') is 0, not NaN, and Number() trims whitespace before coercing -- so a whitespace-only
  // string parses to a finite 0 same as an empty one. Checking the trimmed string for emptiness
  // catches both.
  const usable = trimmed !== '' && Number.isFinite(parsed);
  return usable ? parsed : (description?.baths ?? '');
};

export function mapApiToForm(d, fallbackLabel) {
  const allSchools = d?.schools?.schools ?? [];
  const find = (level) => allSchools.find(s => s.education_levels?.[0] === level);
  const cleanName = (s, suffix) => s?.name?.replace(new RegExp(suffix, 'i'), '').trim() ?? '';
  const transformDetails = (details) => {
    if (!details?.length) return {};
    return details.reduce((acc, entry) => {
      // A real detail response can carry a null entry, or an entry with no category. Every other
      // read in this function is defensive about a missing field; skipping here instead of throwing
      // keeps one bad entry from losing beds/baths/sqft/price/schools -- everything mapApiToForm
      // produces -- since a throw here propagates out of the whole function before any field is
      // assigned.
      const category = entry?.category;
      if (!category) return acc;
      acc[category.toLowerCase().replace(/\s+/g, '_')] = entry.text;
      return acc;
    }, {});
  }

  return {
    address: {
      street: d?.location?.address?.line ?? fallbackLabel,
      city: d?.location?.address?.city ?? '',
      state: d?.location?.address?.state_code ?? '',
      zip: d?.location?.address?.postal_code ?? '',
      coordinates: {
        lat: d?.location?.address?.coordinate?.lat ?? null,
        lon: d?.location?.address?.coordinate?.lon ?? null,
      },
    },
    propertyType: PROPERTY_TYPE_LABELS[d?.description?.type] ?? '',
    price: d?.list_price ?? '',
    history: d?.property_history?.map( h => {
        return { date: h.date, event_name: h.event_name, price: h.price, mls_source: h.source_name }
    }),
    details: {
      beds: d?.description?.beds ?? '',
      baths: resolveBaths(d?.description),
      sqft: d?.description?.sqft ?? '',
      lotSize: d?.description?.lot_sqft
        ? (d.description.lot_sqft / 43560).toFixed(2)
        : '',
      yearBuilt: d?.description?.year_built ?? '',
      parking: d?.description?.garage != null ? 'garage' : '',
      hoaFee: d?.hoa?.fee ?? '',
      floodZone: d?.local?.flood?.fema_zone?.[0] ?? '',
      sewer: '',
      amenities: transformDetails(d?.details),
      tax: {
        amount: d?.tax_history?.[0]?.tax ?? '',
        year: d?.tax_history?.[0]?.year ?? '',
      },
      schools: {
        elementary: cleanName(find('elementary'), 'Elementary School'),
        elementary_state_rate: find('elementary')?.rating ?? '',
        middle: cleanName(find('middle'), 'Middle School'),
        middle_state_rate: find('middle')?.rating ?? '',
        high: cleanName(find('high'), 'High School'),
        high_state_rate: find('high')?.rating ?? '',
      },
    },
  };
}

// Same bounds as reilm-be's RECOMPUTE_CRITERIA_LIMITS and PropertyDashboard.jsx's own BEDS_FIELD_MIN/
// MAX etc: these feed calcScore/compAdjustments directly whenever arv.criteria's own min is empty, so
// an unchecked typo at creation time reaches the ARV estimate unbounded.
// beds carries integer: true, same as fieldLimits.js's own beds entry -- real provider comp data is
// beds-integer-only, so a fractional value has no comp to match and 400s on the backend's
// assertRecomputeCriteriaBody rule. baths legitimately takes 0.5 steps and stays without the flag.
const DETAIL_FIELD_BOUNDS = {
  beds: { min: 0, max: 15, integer: true },
  baths: { min: 0, max: 15 },
  sqft: { min: 0, max: 10000 },
  yearBuilt: { min: 1800, max: 2099 },
};

// Reject, not clamp -- same rule as PropertyDashboard.jsx's Major Systems years. Blank passes: every
// one of these fields is optional here.
const isDetailFieldValid = (field, value) => {
  if (value === '' || value == null) return true;
  const { min, max, integer } = DETAIL_FIELD_BOUNDS[field];
  const n = Number(value);
  if (!Number.isFinite(n) || n < min || n > max) return false;
  return !integer || Number.isInteger(n);
};

// The create body the drawer used to build before its inputs were removed (SOW 4): the blank form,
// the provider detail mapped over it, and out-of-range numbers nulled -- a provider value never passed
// through an input, so the bound check here is the only one it gets.
export function buildCreateBody(mapped) {
  const form = { ...initialForm, ...mapped };
  const details = { ...initialForm.details, ...(mapped?.details ?? {}) };
  for (const field of Object.keys(DETAIL_FIELD_BOUNDS)) {
    if (!isDetailFieldValid(field, details[field])) details[field] = null;
  }
  return { propertyDetails: { ...form, details } };
}

// Values: files/tasks/sow4/add-property-spec.md (add-property frame, export 15.10).
const SHADOW = '0 2px 8px rgba(0,0,0,0.1)';
const FONT = { fontFamily: 'Figtree, sans-serif', fontSize: '13px' };
const menuTextStyle = (base) => ({
  ...base, ...FONT, color: '#8A9A85', textAlign: 'left', padding: '0 22px', lineHeight: '24px',
});

const rsStyles = {
  // Static so the menu positions against the pill, not the input: the dropdown is drawn 36px in from
  // the pill's left edge and flush with its right edge.
  container: (base) => ({ ...base, position: 'static', flex: 1, minWidth: 0 }),
  control: (base) => ({
    ...base, minHeight: '38px', height: '38px', border: 'none', boxShadow: 'none',
    backgroundColor: 'transparent', cursor: 'text',
  }),
  valueContainer: (base) => ({ ...base, padding: 0 }),
  input: (base) => ({ ...base, ...FONT, margin: 0, padding: 0, color: '#17270D' }),
  placeholder: (base) => ({ ...base, ...FONT, margin: 0, color: '#8A9A85' }),
  singleValue: (base) => ({ ...base, ...FONT, margin: 0, color: '#17270D' }),
  // Offsets are from the pill's padding box, 1px inside its border: 36px / 6px from the outer edge.
  menu: (base) => ({
    ...base, top: 'calc(100% + 7px)', left: '35px', right: '-1px', width: 'auto', marginTop: 0,
    border: '1px solid #4CBC18', borderRadius: '8px', boxShadow: SHADOW, backgroundColor: 'white',
    overflow: 'hidden', zIndex: 50,
  }),
  menuList: (base) => ({ ...base, padding: '6px 0', display: 'flex', flexDirection: 'column', gap: '4px' }),
  option: (base, state) => ({
    ...base, ...FONT, height: '24px', lineHeight: '24px', padding: '0 22px', color: '#17270D',
    backgroundColor: state.isFocused ? '#DDE9D8' : 'white', cursor: 'pointer',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
    ':active': { backgroundColor: '#DDE9D8' },
  }),
  noOptionsMessage: menuTextStyle,
  loadingMessage: menuTextStyle,
};

const NO_INDICATORS = {
  DropdownIndicator: null, IndicatorSeparator: null, ClearIndicator: null, LoadingIndicator: null,
};

function ExpandedToolbar({ onCollapse }) {
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);
  const [mapped, setMapped] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  // A failed address lookup used to render as "No results", identical to a genuine no-match, which
  // is how a broken lookup reached a review round with the wrong cause attached to it.
  const [searchFailed, setSearchFailed] = useState(false);
  // The backend retries the detail call once (route.js isDownstreamFlake), so this only fires once
  // that retry is also exhausted. The create still goes ahead with the street from the label.
  const [detailFailed, setDetailFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const debounceTimer = useRef(null);
  const menuOpenRef = useRef(false);
  // Only the latest selection's detail response may land: picking B while A's lookup is in flight
  // must not let A's late answer become what Add & Open posts.
  const detailSeqRef = useRef(0);
  // A ref, not state: two clicks (or click + Enter) can land in the same tick, before a state update
  // has flushed, and nothing downstream de-dupes a create. Set synchronously before the first await.
  const savingRef = useRef(false);
  const selectRef = useRef(null);
  // A create still in flight when the user leaves the page must not pull them to the new dashboard.
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);
  // isDisabled drops focus for the duration of a save; put it back so Enter retries and Esc works.
  const wasSavingRef = useRef(false);
  useEffect(() => {
    if (wasSavingRef.current && !saving) selectRef.current?.focus();
    wasSavingRef.current = saving;
  }, [saving]);

  const canSubmit = !!selected && !!mapped && !detailLoading && !saving;

  const loadOptions = useCallback((inputValue, callback) => {
    clearTimeout(debounceTimer.current);
    if (inputValue.length <= 3) { callback([]); return; }
    debounceTimer.current = setTimeout(async () => {
      try {
        const { data } = await reilmAPI.searchAddress(inputValue);
        // Our backend re-wraps RapidAPI's own { data, status, message } envelope, so the payload
        // sits three levels deep.
        const opts = data?.data?.data?.autocomplete
          ?.filter(a => a.area_type === 'address')
          ?.map(a => ({ value: a.id, label: a?.full_address?.[0] })) ?? [];
        setSearchFailed(false);
        callback(opts);
      } catch {
        setSearchFailed(true);
        callback([]);
      }
    }, 800);
  }, []);

  const clearSelection = () => {
    detailSeqRef.current += 1;
    setSelected(null);
    setMapped(null);
    setDetailLoading(false);
    setDetailFailed(false);
  };

  const onAddressChange = async (option) => {
    if (!option) { clearSelection(); return; }
    const seq = ++detailSeqRef.current;
    setSelected(option);
    setMapped(null);
    setDetailLoading(true);
    setDetailFailed(false);
    setSaveError(null);
    try {
      const { data } = await reilmAPI.realtorPropertyDetail(option.value);
      if (seq !== detailSeqRef.current) return;
      setMapped(mapApiToForm(data?.data?.data, option.label));
    } catch (err) {
      if (seq !== detailSeqRef.current) return;
      // A 401 means the interceptor is already redirecting to login; no message on the way out.
      if (err?.response?.status === 401) return;
      setMapped({ address: { ...initialForm.address, street: option.label } });
      setDetailFailed(true);
    } finally {
      if (seq === detailSeqRef.current) setDetailLoading(false);
    }
  };

  // Typing a new query hides the selected value, so it stops being what Add & Open would post.
  const onInputChange = (value, { action }) => {
    if (action === 'input-change' && value && selected) clearSelection();
  };

  const handleAddAndOpen = async () => {
    if (savingRef.current || !canSubmit) return;
    savingRef.current = true;
    setSaving(true);
    setSaveError(null);
    try {
      const { data } = await reilmAPI.savePropertyDetails(buildCreateBody(mapped));
      if (!mountedRef.current) return;
      const id = data?.data?.property?._id;
      if (!id) throw new Error('missing id');
      navigate(`/property/${id}`);
    } catch (err) {
      // 401 and the subscription gate's 403 are answered by the interceptor (login / expired popup).
      if (!mountedRef.current || isHandledElsewhere(err)) return;
      setSaveError(err?.response?.data?.error?.message || 'Failed to save property. Please try again.');
    } finally {
      // Released so a failed create can be retried from the same toolbar.
      savingRef.current = false;
      if (mountedRef.current) setSaving(false);
    }
  };

  const collapse = () => {
    if (savingRef.current) return;
    clearTimeout(debounceTimer.current);
    onCollapse();
  };

  // Capture phase, so Esc collapses even while react-select would otherwise use it to close its menu.
  const onKeyDownCapture = (e) => {
    if (e.key !== 'Escape') return;
    e.preventDefault();
    e.stopPropagation();
    collapse();
  };

  // A highlighted suggestion in an open menu is react-select's own Enter (it picks it). Anything else,
  // including an open menu with no options ("Type 4+ characters"), is Add & Open. focusedOption is
  // react-select 5's internal state; the menu-open flag guards against a stale value once it closes.
  const onSelectKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    if (menuOpenRef.current && selectRef.current?.state?.focusedOption) return;
    e.preventDefault();
    handleAddAndOpen();
  };

  const message = saveError || (detailFailed ? 'Could not load property details, try again' : null);

  return (
    <div className="flex flex-col max-w-full" onKeyDownCapture={onKeyDownCapture}>
      <div
        className="relative flex items-center h-10 w-[550px] max-w-full rounded-[20px] border border-brand-green bg-white pl-[58px] pr-[16px] shadow-[0_2px_8px_rgba(0,0,0,0.1)]"
        data-testid="add-property-toolbar"
      >
        <AddPropertyIcon className="absolute -left-px -top-[3px] pointer-events-none" />
        <AsyncSelect
          ref={selectRef}
          aria-label="Property address"
          autoFocus
          value={selected}
          loadOptions={loadOptions}
          onChange={onAddressChange}
          onInputChange={onInputChange}
          onKeyDown={onSelectKeyDown}
          onMenuOpen={() => { menuOpenRef.current = true; }}
          onMenuClose={() => { menuOpenRef.current = false; }}
          isDisabled={saving}
          placeholder="Enter a property address..."
          noOptionsMessage={({ inputValue }) => {
            if (inputValue.length <= 3) return 'Type 4+ characters to search';
            return searchFailed ? 'Address lookup unavailable, try again' : 'No results';
          }}
          styles={rsStyles}
          components={NO_INDICATORS}
        />
        <button
          type="button"
          onClick={handleAddAndOpen}
          disabled={!canSubmit}
          className={`ml-2 shrink-0 h-7 w-24 rounded-[14px] text-[13px] font-semibold text-white transition-colors ${
            canSubmit ? 'bg-brand-green hover:bg-brand-hover' : 'bg-brand-disabled cursor-not-allowed'
          }`}
        >
          Add &amp; Open
        </button>
        <button
          type="button"
          aria-label="Close Add Property"
          onClick={collapse}
          disabled={saving}
          className="ml-[2px] shrink-0 flex items-center justify-center w-6 h-6 text-brand-dark hover:text-brand-hover disabled:cursor-not-allowed"
        >
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
            <path d="M9 1L1 9M9 9L1 1" stroke="currentColor" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
      {message && <p className="text-[12px] text-red-400 mt-[6px] pl-[58px]">{message}</p>}
    </div>
  );
}

// Inline Add Property in the Pipeline toolbar: a button that expands into the address bar. Expanded
// state lives in the parent, which hides Delete while it is open.
function AddOpportunity({ expanded, onExpandedChange }) {
  const buttonRef = useRef(null);
  const returnFocusRef = useRef(false);
  useEffect(() => {
    if (!expanded && returnFocusRef.current) {
      returnFocusRef.current = false;
      buttonRef.current?.focus();
    }
  }, [expanded]);

  if (expanded) {
    return (
      <ExpandedToolbar
        onCollapse={() => { returnFocusRef.current = true; onExpandedChange(false); }}
      />
    );
  }
  return (
    <button
      ref={buttonRef}
      type="button"
      aria-expanded={false}
      onClick={() => onExpandedChange(true)}
      className="relative flex items-center h-10 w-[148px] shrink-0 rounded-[20px] bg-brand-green pl-[52px] text-white text-[13px] font-semibold shadow-[0_2px_8px_rgba(0,0,0,0.1)] outline-none focus-visible:ring-2 focus-visible:ring-brand-green focus-visible:ring-offset-2 hover:opacity-90 transition-opacity"
    >
      <AddPropertyIcon className="absolute left-0 -top-[2px] pointer-events-none" />
      Add Property
    </button>
  );
}

export default AddOpportunity;
